/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { Button } from '@mui/material';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import CustomDialog from 'components/modals/common/CustomDialog';
import { MdModelHeader } from 'models/md/MdModel';
import { findMdModelBySpec, findMdModelHeaders } from 'apis/md/MdModel';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { CommonUtil } from 'utils/CommonUtil';

type Props = {
  open: boolean;
  close: () => void;
};

/**
 * @desc 투자비관리 > 기준정보 > 모델 등록/조회 > 모델 전체요약 팝업
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.13   jshwang12       신규 생성
 */
const ModelSummaryPopup = ({ open, close, ...props }: Props) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const [code, setCode] = useState<any>();
  const [rowData, setRowData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<MdModelHeader[]>([]);
  const [gridHeight, setGridHeight] = useState<number>();

  useEffect(() => {
    getCommonCodes();
    getGridHeaders();
    handleSearch();
  }, []);

  useEffect(() => {
    changeGridHeight((headers || []).length);
  }, [headers]);

  const changeGridHeight = useEvent((rowCount = 0) => {
    const rowHeight = 37;
    const defHeight = 250;
    if (rowCount < 1) {
      setGridHeight(defHeight);
    } else {
      setGridHeight(Math.max(rowHeight * rowCount + 6, defHeight));
    }
  });

  /**
   * 그리드 헤더 동적으로 그리기 위해 목록 조회
   */
  const getGridHeaders = () => {
    findMdModelHeaders().then((result) => {
      setHeaders(result);
    });
  };

  const getCommonCodes = async () => {
    const mdlSpecCd: Code[] = await getCommonCodeNamesCondition({
      cmnGrCd: 'MDL_SPEC_CD',
      optValCtn7: 'Y', // 모델 속성여부
    });
    // 사양코드 값 그룹코드 목록
    const colGrCds = (mdlSpecCd || []).reduce((acc: string[], cur) => {
      if (cur.optValCtn4 && !acc.includes(cur.optValCtn4)) {
        acc.push(cur.optValCtn4);
      }
      return acc;
    }, []);
    // 사양코드 값 그룹코드 값 조회
    const colGrCdsResults = await Promise.all(
      colGrCds.map((cmnGrCd) => getCommonCodeNames(cmnGrCd))
    );
    const mapColGrCds = colGrCds.reduce((acc, cmnGrCd, index) => {
      acc[cmnGrCd] = colGrCdsResults[index];
      return acc;
    }, {} as Record<string, Code[]>);

    setCode({
      colGrCds: mapColGrCds, // 그리드 셀 콤보박스에서 사용되는 공통코드 (ex. {MD_CELL_TP: [] as Code[], ...})
    });
  };

  /**
   * 조회
   */
  const handleSearch = () => {
    findMdModelBySpec().then((res) => {
      setRowData(res);
    });
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('md.label.모델 전체 요약', '모델 전체 요약'),
      t('md.label.모델 목록', '모델 목록')
    );
  };

  const gridData = useMemo(() => {
    return (headers || []).map((header) => {
      const item = {
        ctgrCd: header.ctgrCd,
        ctgrNm: header.ctgrNm || header.specNm, // 카테고리 없는 경우 셀병합을 위해 동일한 값 사용
        specCd: header.specCd,
        specNm:
          header.specCd === 'S00115' ? String(t('md.label.압연사양', '압연사양')) : header.specNm,
        specValTp: header.specValTp,
        specValCd: header.specValCd,
      };
      (rowData || []).forEach((o) => {
        let specVal = header.specCd ? o[header.specCd] : '';
        if (header.specValTp === 'LIST' && header.specValCd && specVal) {
          const cd = (code?.colGrCds?.[header.specValCd] || []).filter((o) => o.cmnCd === specVal);
          if (cd.length) {
            specVal = cd[0].cmnCdNm;
          }
        }
        item[o['MDL_ID']] = specVal;
      });
      return item;
    });
  }, [code, rowData, headers]);

  const layoutDefinition = useMemo(() => {
    return [
      {
        header: String(t('com.label.구분', '구분')),
        columns: [
          {
            binding: 'ctgrNm',
            header: String(t('com.label.구분', '구분')),
            align: 'center',
            allowMerging: true,
            cssClass: 'text-center transposed-header',
            width: 80,
            minWidth: 50,
          },
        ],
      },
      {
        binding: 'specNm',
        header: String(t('com.label.구분', '구분')),
        align: 'center',
        allowMerging: true,
        width: 100,
        minWidth: 80,
        cssClass: 'text-center transposed-header',
      },
      {
        header: String(t('md.label.모델', '모델')),
        columns: (rowData || []).map((o) => ({
          binding: o['MDL_ID'],
          header: o['MDL_NM'],
          align: 'center',
          width: 92,
          minWidth: 50,
        })),
      },
    ];
  }, [rowData]);

  const onInitialized = useEvent((grid) => {
    gridRef.current = grid;
  });

  return (
    <CustomDialog
      title={String(t('md.label.모델 전체 요약', '모델 전체 요약'))}
      size={'lg'}
      open={open}
      onClose={close}
    >
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.모델 List', '모델 List')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={() => handleSearch()}>
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={`mdModelSummaryPopupGrid`}
        layoutDefinition={layoutDefinition}
        rowData={gridData}
        height={gridHeight}
        frozenColumns={2}
        isReadOnly={true}
        isSelector={false}
        isFilter={false}
        allowPinning={false}
        initialized={onInitialized}
      />
    </CustomDialog>
  );
};

export default ModelSummaryPopup;
